import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft, LogOut, Loader2, AlertCircle, Trash2, RefreshCw,
  Search, Phone, Mail, MapPin, Calendar, DollarSign, Users,
  Star, Check, X, Inbox,
} from "lucide-react";
import {
  login, me, listLeads, deleteLead, logout, getToken,
  listReviews, approveReview, deleteReview,
} from "../lib/adminApi";

const Admin = () => {
  const [authed, setAuthed] = useState(false);
  const [bootChecking, setBootChecking] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listErr, setListErr] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  // Reviews
  const [tab, setTab] = useState("leads"); // 'leads' | 'reviews'
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Boot: try existing token
  useEffect(() => {
    (async () => {
      if (!getToken()) {
        setBootChecking(false);
        return;
      }
      try {
        await me();
        setAuthed(true);
      } catch {
        logout();
      } finally {
        setBootChecking(false);
      }
    })();
  }, []);

  const loadLeads = async () => {
    setLoading(true);
    setListErr("");
    try {
      const data = await listLeads();
      setLeads(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        setAuthed(false);
      } else {
        setListErr("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) loadLeads();
  }, [authed]);

  const loadReviewsClick = async () => {
    setReviewsLoading(true);
    try {
      const data = await listReviews();
      setReviews(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        setAuthed(false);
      }
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    if (authed && tab === "reviews") {
      (async () => {
        setReviewsLoading(true);
        try {
          const data = await listReviews();
          setReviews(data);
        } catch (e) {
          if (e?.response?.status === 401) {
            logout();
            setAuthed(false);
          }
        } finally {
          setReviewsLoading(false);
        }
      })();
    }
  }, [authed, tab]);

  const onApprove = async (r, approved) => {
    try {
      const updated = await approveReview(r.id, approved);
      setReviews((rs) => rs.map((x) => (x.id === r.id ? updated : x)));
    } catch {
      alert("Failed to update review.");
    }
  };

  const onDeleteReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await deleteReview(id);
      setReviews((rs) => rs.filter((r) => r.id !== id));
    } catch {
      alert("Failed to delete review.");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoginErr("");
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      setAuthed(true);
    } catch (err) {
      setLoginErr(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setSubmitting(false);
    }
  };

  const onLogout = () => {
    logout();
    setAuthed(false);
    setLeads([]);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await deleteLead(id);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch {
      alert("Failed to delete.");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
      [l.name, l.email, l.phone, l.state, l.product_interest, l.face_amount, l.inquiring_for, l.beneficiary]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [leads, query]);

  if (bootChecking) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gold" />
      </div>
    );
  }

  // -------------- LOGIN VIEW --------------
  if (!authed) {
    return (
      <div className="min-h-screen bg-aurora ivory-pattern flex items-center justify-center px-6 py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-30" />
        <div className="absolute top-1/3 right-10 w-80 h-80 bg-gold/15 rounded-full blur-3xl" />

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          onSubmit={onSubmit}
          noValidate
          className="relative w-full max-w-md glass p-10"
          data-testid="admin-login-form"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-ink/55 hover:text-gold transition-colors mb-6" data-testid="admin-back-home">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-ink flex items-center justify-center font-display text-gold">Z</div>
            <div>
              <div className="font-display text-ink text-xl">Admin Console</div>
              <div className="eyebrow text-gold text-[10px]">Zaylan Welton · Leads</div>
            </div>
          </div>

          <div className="space-y-6">
            <Field id="admin-username" label="Username" value={username} onChange={(e) => setUsername(e.target.value)} />
            <Field id="admin-password" label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>

          {loginErr && (
            <div data-testid="admin-login-error" className="mt-6 flex items-start gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{loginErr}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="mt-8 w-full group relative inline-flex items-center justify-center gap-3 bg-ink text-gold-light px-6 py-4 text-sm font-semibold tracking-[0.2em] uppercase overflow-hidden disabled:opacity-50"
          >
            <span className="absolute inset-0 bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
            {submitting ? <Loader2 className="relative w-4 h-4 animate-spin" /> : <span className="relative group-hover:text-ink transition-colors">Sign In</span>}
          </button>
        </motion.form>
      </div>
    );
  }

  // -------------- DASHBOARD VIEW --------------
  return (
    <div className="min-h-screen bg-ivory">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-ivory/90 backdrop-blur-xl border-b border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-ink flex items-center justify-center font-display text-gold text-sm">Z</div>
            <div>
              <div className="font-display text-ink text-sm leading-tight">Admin Console</div>
              <div className="eyebrow text-gold text-[9px]">Lead Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => (tab === "leads" ? loadLeads() : loadReviewsClick())} data-testid="admin-refresh" className="inline-flex items-center gap-2 text-xs text-ink/70 hover:text-gold transition-colors px-3 py-2">
              <RefreshCw className={`w-3.5 h-3.5 ${(loading || reviewsLoading) ? "animate-spin" : ""}`} /> Refresh
            </button>
            <Link to="/" className="inline-flex items-center gap-2 text-xs text-ink/70 hover:text-gold transition-colors px-3 py-2" data-testid="admin-view-site">
              <ArrowLeft className="w-3.5 h-3.5" /> Site
            </Link>
            <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-xs text-ivory bg-ink hover:bg-gold hover:text-ink px-3 py-2 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Tab switcher */}
        <div className="mb-8 flex border-b border-line">
          <button
            onClick={() => setTab("leads")}
            data-testid="tab-leads"
            className={`px-5 py-3 text-xs tracking-[0.25em] uppercase font-semibold transition-colors border-b-2 -mb-px ${
              tab === "leads" ? "text-ink border-gold" : "text-ink/45 border-transparent hover:text-ink/70"
            }`}
          >
            <span className="inline-flex items-center gap-2"><Inbox className="w-3.5 h-3.5" /> Leads ({leads.length})</span>
          </button>
          <button
            onClick={() => setTab("reviews")}
            data-testid="tab-reviews"
            className={`px-5 py-3 text-xs tracking-[0.25em] uppercase font-semibold transition-colors border-b-2 -mb-px ${
              tab === "reviews" ? "text-ink border-gold" : "text-ink/45 border-transparent hover:text-ink/70"
            }`}
          >
            <span className="inline-flex items-center gap-2">
              <Star className="w-3.5 h-3.5" /> Reviews ({reviews.length})
              {reviews.some((r) => !r.approved) && (
                <span className="ml-1 inline-flex items-center justify-center bg-gold text-ink text-[10px] px-1.5 py-0.5 leading-none">
                  {reviews.filter((r) => !r.approved).length}
                </span>
              )}
            </span>
          </button>
        </div>

        {tab === "leads" && (
          <>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8">
          <div>
            <h1 className="font-display text-4xl text-ink leading-tight font-light">
              Leads <span className="text-gold">({leads.length})</span>
            </h1>
            <p className="text-ink/60 text-sm mt-2">Real-time submissions from your landing page.</p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, state…"
              data-testid="admin-search"
              className="w-full bg-white border border-line focus:border-gold pl-9 pr-3 py-2.5 text-sm text-ink outline-none transition-colors"
            />
          </div>
        </div>

        {listErr && (
          <div className="mb-4 text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {listErr}
          </div>
        )}

        {loading && leads.length === 0 ? (
          <div className="text-center py-20 text-ink/50"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-line p-12 text-center text-ink/55">
            <div className="font-display text-2xl text-ink/40 mb-2">No leads yet</div>
            <p className="text-sm">When someone submits the form, they&apos;ll appear here in real time.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* List */}
            <div className="lg:col-span-2 space-y-3" data-testid="admin-leads-list">
              {filtered.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setSelected(l)}
                  data-testid={`lead-row-${l.id}`}
                  className={`w-full text-left bg-white border ${selected?.id === l.id ? "border-gold shadow-md" : "border-line hover:border-gold/60"} p-5 transition-all`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-lg text-ink">{l.name}</div>
                      <div className="text-xs text-ink/55 mt-0.5">{new Date(l.created_at).toLocaleString()}</div>
                    </div>
                    {l.product_interest && (
                      <span className="text-[10px] tracking-[0.2em] uppercase bg-ink text-gold px-2.5 py-1">{l.product_interest}</span>
                    )}
                  </div>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-ink/70">
                    <Cell icon={<Mail className="w-3 h-3" />} value={l.email} />
                    <Cell icon={<Phone className="w-3 h-3" />} value={l.phone} />
                    <Cell icon={<MapPin className="w-3 h-3" />} value={l.state || "—"} />
                    <Cell icon={<DollarSign className="w-3 h-3" />} value={l.face_amount || "—"} />
                  </div>
                </button>
              ))}
            </div>

            {/* Detail panel */}
            <aside className="lg:col-span-1">
              {selected ? (
                <div className="bg-white border border-line p-6 sticky top-24" data-testid="lead-detail">
                  <div className="flex items-start justify-between gap-3 mb-5">
                    <div>
                      <div className="eyebrow text-gold text-[10px]">Lead Detail</div>
                      <div className="font-display text-2xl text-ink leading-tight mt-1">{selected.name}</div>
                    </div>
                    <button onClick={() => onDelete(selected.id)} data-testid="lead-delete" className="text-ink/40 hover:text-red-600 transition-colors p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <Row label="Submitted" icon={<Calendar className="w-3.5 h-3.5" />} value={new Date(selected.created_at).toLocaleString()} />
                  <Row label="Email" icon={<Mail className="w-3.5 h-3.5" />} value={selected.email} link={`mailto:${selected.email}`} />
                  <Row label="Phone" icon={<Phone className="w-3.5 h-3.5" />} value={selected.phone} link={`tel:${selected.phone}`} />
                  <Row label="Age" value={selected.age || "—"} />
                  <Row label="Type" value={selected.product_interest || "—"} />
                  <Row label="Face Amount" icon={<DollarSign className="w-3.5 h-3.5" />} value={selected.face_amount || "—"} />
                  <Row label="Inquiring For" icon={<Users className="w-3.5 h-3.5" />} value={selected.inquiring_for || "—"} />
                  <Row label="Beneficiary" value={selected.beneficiary || "—"} />
                  <Row label="State" icon={<MapPin className="w-3.5 h-3.5" />} value={selected.state || "—"} />
                  {selected.message && (
                    <div className="mt-4 pt-4 border-t border-line">
                      <div className="eyebrow text-ink/50 text-[10px] mb-2">Notes</div>
                      <p className="text-sm text-ink/80 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                    </div>
                  )}
                  <div className="mt-6 flex flex-col gap-2">
                    <a href={`mailto:${selected.email}`} className="text-center bg-ink text-gold-light hover:bg-gold hover:text-ink py-2.5 text-xs tracking-[0.2em] uppercase font-semibold transition-colors">Reply by Email</a>
                    <a href={`tel:${selected.phone}`} className="text-center bg-gold text-ink hover:bg-ink hover:text-gold py-2.5 text-xs tracking-[0.2em] uppercase font-semibold transition-colors">Call Now</a>
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-line p-6 text-ink/45 text-sm text-center">
                  Select a lead to view details
                </div>
              )}
            </aside>
          </div>
        )}
          </>
        )}

        {tab === "reviews" && (
          <div data-testid="reviews-panel">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h1 className="font-display text-4xl text-ink leading-tight font-light">
                  Reviews <span className="text-gold">({reviews.length})</span>
                </h1>
                <p className="text-ink/60 text-sm mt-2">
                  Approve to display on your landing page. Pending count: {reviews.filter((r) => !r.approved).length}.
                </p>
              </div>
            </div>

            {reviewsLoading && reviews.length === 0 ? (
              <div className="text-center py-20 text-ink/50"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : reviews.length === 0 ? (
              <div className="bg-white border border-line p-12 text-center text-ink/55">
                <div className="font-display text-2xl text-ink/40 mb-2">No reviews yet</div>
                <p className="text-sm">When clients submit a review, they&apos;ll appear here for your approval.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {reviews.map((r) => (
                  <div
                    key={r.id}
                    data-testid={`review-row-${r.id}`}
                    className={`bg-white border ${r.approved ? "border-gold/50" : "border-line"} p-6 flex flex-col`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star
                            key={i}
                            className={`w-3.5 h-3.5 ${i <= r.rating ? "fill-gold text-gold" : "fill-transparent text-ink/25"}`}
                          />
                        ))}
                      </div>
                      {r.approved ? (
                        <span className="text-[10px] tracking-[0.2em] uppercase bg-gold text-ink px-2 py-0.5 font-semibold">
                          Live
                        </span>
                      ) : (
                        <span className="text-[10px] tracking-[0.2em] uppercase bg-ink text-gold px-2 py-0.5 font-semibold">
                          Pending
                        </span>
                      )}
                    </div>
                    <p className="mt-4 text-sm text-ink/80 leading-relaxed flex-grow">
                      &ldquo;{r.body}&rdquo;
                    </p>
                    <div className="mt-4 pt-3 border-t border-line">
                      <div className="font-display text-base text-ink">{r.name}</div>
                      <div className="text-[10px] eyebrow text-ink/45 mt-0.5">
                        {(r.location || "—")} · {new Date(r.created_at).toLocaleDateString()}
                      </div>
                      {r.email && (
                        <div className="text-xs text-ink/55 mt-1">
                          <Mail className="w-3 h-3 inline mr-1 text-gold" />
                          <a href={`mailto:${r.email}`} className="hover:text-gold">{r.email}</a>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 flex gap-2">
                      {r.approved ? (
                        <button
                          onClick={() => onApprove(r, false)}
                          data-testid={`review-unapprove-${r.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 border border-line hover:border-ink text-ink py-2 text-[11px] tracking-[0.2em] uppercase font-semibold transition-colors"
                        >
                          <X className="w-3 h-3" /> Unpublish
                        </button>
                      ) : (
                        <button
                          onClick={() => onApprove(r, true)}
                          data-testid={`review-approve-${r.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-ink text-gold-light hover:bg-gold hover:text-ink py-2 text-[11px] tracking-[0.2em] uppercase font-semibold transition-colors"
                        >
                          <Check className="w-3 h-3" /> Approve
                        </button>
                      )}
                      <button
                        onClick={() => onDeleteReview(r.id)}
                        data-testid={`review-delete-${r.id}`}
                        className="px-3 inline-flex items-center justify-center border border-line hover:border-red-500 hover:text-red-600 text-ink/50 py-2 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

const Field = ({ label, id, ...rest }) => (
  <div>
    <label htmlFor={id} className="eyebrow text-ink/55 text-[10px] mb-2 block">{label}</label>
    <input
      id={id}
      data-testid={id}
      autoComplete="off"
      {...rest}
      className="w-full bg-transparent border-b border-line focus:border-gold text-ink py-3 outline-none transition-colors font-light"
    />
  </div>
);

const Cell = ({ icon, value }) => (
  <div className="flex items-center gap-1.5 truncate">
    <span className="text-gold shrink-0">{icon}</span>
    <span className="truncate">{value}</span>
  </div>
);

const Row = ({ label, value, icon, link }) => (
  <div className="py-2.5 border-b border-line last:border-0 grid grid-cols-3 gap-3 items-start">
    <div className="eyebrow text-ink/55 text-[10px] flex items-center gap-1.5">
      {icon && <span className="text-gold">{icon}</span>} {label}
    </div>
    <div className="col-span-2 text-sm text-ink break-words">
      {link ? <a href={link} className="hover:text-gold transition-colors">{value}</a> : value}
    </div>
  </div>
);

export default Admin;
